import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { queryKeys } from "@/shared/utils/queryKeys";

export interface PlaidTransaction {
  id: number;
  estate_id: number;
  plaid_connection_id: number;
  plaid_transaction_id: string;
  plaid_account_id: string;
  date: string;
  description: string;
  original_description: string | null;
  merchant_name: string | null;
  amount: number;
  is_inflow: boolean;
  pending: boolean;
  category: string | null;
  has_receipt: boolean;
  authorized_date: string | null;
  payment_channel: string | null;
  check_number: string | null;
  reference_number: string | null;
  counterparty_name: string | null;
  notes: string | null;
  receipt_document_id: number | null;
  linked_asset_id: number | null;
  recipient_contact_id: number | null;
  created_at: string;
  updated_at: string;
}

interface TransactionsResponse {
  transactions: PlaidTransaction[];
}

interface SyncResponse {
  synced_count: number;
  removed_count: number;
}

async function fetchTransactions(
  estateId: number,
  connectionId: number,
): Promise<PlaidTransaction[]> {
  const { data, error } = await supabase.functions.invoke(
    `financials/${estateId}/banking/${connectionId}/transactions`,
    { method: "GET" },
  );

  if (error) throw new Error(error.message || "Failed to fetch transactions");
  return (data as TransactionsResponse).transactions;
}

async function syncTransactions(
  estateId: number,
  connectionId: number,
): Promise<SyncResponse> {
  const { data, error } = await supabase.functions.invoke(
    `financials/${estateId}/banking/${connectionId}/transactions/sync`,
    { method: "POST" },
  );

  if (error) throw new Error(error.message || "Failed to sync transactions");
  return data as SyncResponse;
}

export function useTransactions(
  estateId: number | null,
  connectionId: number | null,
) {
  const queryClient = useQueryClient();
  const enabled = !!estateId && !!connectionId;

  const {
    data: transactions = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: queryKeys.plaid.transactions(estateId!, connectionId!),
    queryFn: () => fetchTransactions(estateId!, connectionId!),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const syncMutation = useMutation({
    mutationFn: (overrideConnectionId?: number) =>
      syncTransactions(estateId!, overrideConnectionId ?? connectionId!),
    onSuccess: (_data, overrideConnectionId) => {
      const connId = overrideConnectionId ?? connectionId!;
      queryClient.invalidateQueries({
        queryKey: queryKeys.plaid.transactions(estateId!, connId),
      });
    },
  });

  return {
    transactions,
    isLoading: enabled && isLoading,
    error,
    syncTransactions: syncMutation.mutateAsync,
    isSyncing: syncMutation.isPending,
  };
}

export interface TransactionUpdate {
  category?: string;
  notes?: string | null;
  receipt_document_id?: number | null;
  linked_asset_id?: number | null;
  recipient_contact_id?: number | null;
  has_receipt?: boolean;
}

async function updateTransaction(
  estateId: number,
  transactionId: number,
  updates: TransactionUpdate,
): Promise<PlaidTransaction> {
  const { data, error } = await supabase.functions.invoke(
    `financials/${estateId}/banking/transactions/${transactionId}`,
    { method: "PUT", body: updates },
  );

  if (error) throw new Error(error.message || "Failed to update transaction");
  return (data as { transaction: PlaidTransaction }).transaction;
}

export function useUpdateTransaction(
  estateId: number | null,
  connectionId: number | null,
) {
  const queryClient = useQueryClient();

  const getQueryKey = () =>
    estateId && connectionId
      ? queryKeys.plaid.transactions(estateId, connectionId)
      : null;

  const updateMutation = useMutation({
    mutationFn: ({ transactionId, updates }: { transactionId: number; updates: TransactionUpdate }) =>
      updateTransaction(estateId!, transactionId, updates),
    onMutate: async ({ transactionId, updates }) => {
      const key = getQueryKey();
      if (!key) return undefined;

      await queryClient.cancelQueries({ queryKey: key });

      const previous = queryClient.getQueryData<PlaidTransaction[]>(key);

      queryClient.setQueryData<PlaidTransaction[]>(key, (old) =>
        old?.map((t) => (t.id === transactionId ? { ...t, ...updates } : t)),
      );

      return { previous, key };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined && context.key) {
        queryClient.setQueryData(context.key, context.previous);
      }
    },
    onSettled: () => {
      const key = getQueryKey();
      if (key) {
        queryClient.invalidateQueries({ queryKey: key });
      }
    },
  });

  return {
    updateTransaction: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
  };
}
